import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { bloque } from '~/libs/bloque';

import {
  getAccountByUrnApi,
  getAccountsApi,
  getBalanceByAccountUrnApi,
} from '../-api/api';
import type { AccountsQueryParams } from '../-types/account';
import type { Transaction } from '../cards/-types/card';

type CardMovementsParams = Parameters<typeof bloque.accounts.card.movements>[0];

export function useAccounts<T>(params: AccountsQueryParams) {
  return useQuery({
    queryKey: ['accounts', params],
    queryFn: () => getAccountsApi<T>(params),
    enabled: !!params.holder_urn,
  });
}

export function useTransactions(
  urn: string,
  asset: string,
  direction?: 'in' | 'out',
) {
  return useInfiniteQuery({
    queryKey: ['transactions', urn, asset, direction],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const result = await bloque.accounts.card.movements({
        urn,
        asset: asset as CardMovementsParams['asset'],
        direction,
        limit: 10,
        next: pageParam,
      });

      return {
        transactions: (result.data ?? []) as Transaction[],
        pageSize: result.pageSize,
        hasMore: result.hasMore,
        next: result.next,
      };
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.next || undefined : undefined,
    enabled: !!urn && !!asset,
  });
}

export function useBalance(urn: string) {
  return useQuery({
    queryKey: ['balance', urn],
    queryFn: () => getBalanceByAccountUrnApi(urn),
    enabled: !!urn,
  });
}

export function useAccountByUrn<T>(urn: string) {
  return useQuery({
    queryKey: ['account', urn],
    queryFn: () => getAccountByUrnApi<T>(urn),
    enabled: !!urn,
  });
}
